import { useState } from "react"
import { ChevronLeft, ChevronRight, Package, Expand, X } from "lucide-react"
import { cn } from "@/lib/utils"
import {  CardContent } from "@/components/ui/card"
import { getInstrumentImageUrl } from "@/Wist-blockchain/collections/instruments/instruments-storage.service"
import { AuthImage } from "./AuthImage"

interface EquipmentImageGalleryProps {
    idFiles: string[]
    equipmentName: string
}

export const EquipmentImageGallery = ({
    idFiles,
    equipmentName,
}: EquipmentImageGalleryProps) => {
    const [activeIndex, setActiveIndex] = useState(0)
    const [lightboxOpen, setLightboxOpen] = useState(false)

    if (!idFiles || idFiles.length === 0) {
        return (
            <div className="flex h-52 items-center justify-center rounded-xl border border-gray-200 bg-white dark:bg-gray-900 text-gray-300">

                <CardContent className="flex h-52 items-center justify-center text-gray-300 p-0">
                    <Package className="h-16 w-16" />
                </CardContent>
            </div>
        )
    }

    const urls = idFiles.map(getInstrumentImageUrl)
    const hasManyImages = urls.length > 1

    const prev = () => setActiveIndex((i) => (i - 1 + urls.length) % urls.length)
    const next = () => setActiveIndex((i) => (i + 1) % urls.length)

    return (
        <>
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:bg-gray-900 dark:border-gray-500">
                {/* Imagen principal */}
                <div className="group relative overflow-hidden bg-gray-50">
                    <AuthImage
                        src={urls[activeIndex]}
                        alt={`${equipmentName} – foto ${activeIndex + 1}`}
                        className="h-56 w-full object-cover rounded-t-xl transition-transform duration-300"
                    />

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-linear-to-t from-black/20 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />

                    {/* Botón expandir */}
                    <button
                        onClick={() => setLightboxOpen(true)}
                        className="absolute right-2.5 top-2.5 flex h-7 w-7 items-center justify-center rounded-md bg-black/50 text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100 hover:bg-black/70 cursor-pointer
                        "
                    >
                        <Expand className="h-3.5 w-3.5" />
                    </button>

                    {/* Flechas */}
                    {hasManyImages && (
                        <>
                            <button
                                onClick={prev}
                                className="absolute left-2.5 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100 hover:bg-black/70 cursor-pointer"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            <button
                                onClick={next}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100 hover:bg-black/70 cursor-pointer"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </>
                    )}

                    {/* Badge contador */}
                    {hasManyImages && (
                        <span className="absolute bottom-2.5 right-2.5 rounded-full bg-black/60 px-2.5 py-0.5 text-xs font-medium text-white backdrop-blur-sm">
                            {activeIndex + 1} / {urls.length}
                        </span>
                    )}
                </div>

                {/* Thumbnails dentro de la card */}
                {hasManyImages && (
                    <div className="flex gap-2 p-3">
                        <div className="flex gap-2">
                            {urls.map((url, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveIndex(i)}
                                    className={cn(
                                        "relative h-14 w-14 shrink-0 overflow-hidden rounded-lg border-2 transition-all duration-200 cursor-pointer",
                                        i === activeIndex
                                            ? "border-blue-500 ring-2 ring-blue-200 ring-offset-1"
                                            : "border-transparent opacity-55 hover:opacity-80 hover:border-gray-300"
                                    )}
                                >
                                    <AuthImage
                                        src={url}
                                        alt={`Foto ${i + 1}`}
                                        className="h-full w-full object-cover"
                                    />
                                    {/* Dot activo */}
                                    {i === activeIndex && (
                                        <span className="absolute bottom-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-blue-500" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Lightbox — renderizado fuera del Card */}
            {lightboxOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm"
                    onClick={() => setLightboxOpen(false)}
                >
                    <div
                        className="relative flex flex-col items-center gap-4 px-4 max-w-4xl w-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Botón cerrar */}
                        <button
                            onClick={() => setLightboxOpen(false)}
                            className="absolute -top-10 right-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer"
                        >
                            <X className="h-4 w-4" />
                        </button>

                        {/* Imagen grande */}
                        <div className="relative w-full">
                            <AuthImage
                                src={urls[activeIndex]}
                                alt={equipmentName}
                                className="max-h-[78vh] w-full rounded-xl object-contain"
                            />
                            {hasManyImages && (
                                <>
                                    <button
                                        onClick={prev}
                                        className="absolute left-3 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/35 transition-colors cursor-pointer"
                                    >
                                        <ChevronLeft className="h-5 w-5" />
                                    </button>
                                    <button
                                        onClick={next}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/35 transition-colors cursor-pointer"
                                    >
                                        <ChevronRight className="h-5 w-5" />
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Thumbnails en lightbox */}
                        {hasManyImages && (
                            <div className="flex gap-2">
                                {urls.map((url, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActiveIndex(i)}
                                        className={cn(
                                            "h-12 w-12 shrink-0 overflow-hidden rounded-lg border-2 transition-all",
                                            i === activeIndex
                                                ? "border-white opacity-100"
                                                : "border-transparent opacity-45 hover:opacity-75"
                                        )}
                                    >
                                        <AuthImage src={url} alt={`Foto ${i + 1}`} className="h-full w-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}